import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft,
  ChevronRight,
  BookOpen,
  FlaskConical,
  GraduationCap,
  Globe,
  Building,
  Users,
  ArrowRight,
  Library,
  Briefcase,
  Award,
} from 'lucide-react';
import Layout from '@/components/Layout';
import { NewsCard, CollegeCard, StatCard, NoticeItem } from '@/components/Cards';
import { ROUTE_PATHS, NEWS_LIST, COLLEGES, STATS, NOTICES, NewsItem } from '@/lib/index';
import { IMAGES } from '@/assets/images';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';

const HERO_SLIDES = [
  {
    image: IMAGES.CAMPUS_1,
    title: '允公允能',
    subtitle: '日新月异',
    description: '南开大学创立于1919年，百余年薪火相传，是国家"双一流A类"重点建设高校。',
    cta1: { label: '了解南开', path: ROUTE_PATHS.ABOUT },
    cta2: { label: '招生信息', path: ROUTE_PATHS.ADMISSIONS },
  },
  {
    image: IMAGES.CAMPUS_7,
    title: '学术卓越',
    subtitle: '勇攀高峰',
    description: '南开大学在化学、数学、物理、经济等多个学科领域处于国内外领先地位，科研成果享誉国际。',
    cta1: { label: '科学研究', path: ROUTE_PATHS.RESEARCH },
    cta2: { label: '院系设置', path: ROUTE_PATHS.ACADEMICS },
  },
  {
    image: IMAGES.GRADUATION_1,
    title: '培育英才',
    subtitle: '服务社会',
    description: '南开大学培育了以周恩来为代表的无数优秀人才，为中华民族的复兴事业作出了重要贡献。',
    cta1: { label: '新闻资讯', path: ROUTE_PATHS.NEWS },
    cta2: { label: '国际合作', path: ROUTE_PATHS.ABOUT },
  },
];

const QUICK_NAV_ITEMS = [
  { label: '学校概况', icon: Building, path: ROUTE_PATHS.ABOUT, color: 'bg-primary/10 text-primary' },
  { label: '院系设置', icon: GraduationCap, path: ROUTE_PATHS.ACADEMICS, color: 'bg-chart-2/10 text-chart-2' },
  { label: '科学研究', icon: FlaskConical, path: ROUTE_PATHS.RESEARCH, color: 'bg-chart-1/10 text-chart-1' },
  { label: '招生信息', icon: BookOpen, path: ROUTE_PATHS.ADMISSIONS, color: 'bg-chart-5/10 text-chart-5' },
  { label: '就业发展', icon: Briefcase, path: ROUTE_PATHS.ADMISSIONS, color: 'bg-chart-4/10 text-chart-4' },
  { label: '图书馆', icon: Library, path: ROUTE_PATHS.ABOUT, color: 'bg-chart-3/10 text-chart-3' },
  { label: '国际合作', icon: Globe, path: ROUTE_PATHS.ABOUT, color: 'bg-primary/10 text-primary' },
  { label: '荣誉成就', icon: Award, path: ROUTE_PATHS.RESEARCH, color: 'bg-chart-1/10 text-chart-1' },
];

const PARTNER_UNIVERSITIES = [
  'Harvard University',
  'MIT',
  'Oxford University',
  'Cambridge University',
  'Stanford University',
  'ETH Zurich',
  'University of Tokyo',
  'Seoul National University',
  'National University of Singapore',
  'University of Melbourne',
  'Yale University',
  'Princeton University',
];

function AnimatedCounter({ target, duration = 2000 }: { target: number; duration?: number }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const inView = useInView(ref, { once: true });

  useEffect(() => {
    if (!inView) return;
    let startTime: number;
    const startValue = 0;
    const endValue = target;

    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(startValue + (endValue - startValue) * eased));
      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [inView, target, duration]);

  return <span ref={ref}>{count.toLocaleString('zh-CN')}</span>;
}

function HeroSection() {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % HERO_SLIDES.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goToSlide = (index: number) => {
    if (isAnimating || index === currentSlide) return;
    setIsAnimating(true);
    setCurrentSlide(index);
    setTimeout(() => setIsAnimating(false), 600);
  };

  const prevSlide = () => goToSlide((currentSlide - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);
  const nextSlide = () => goToSlide((currentSlide + 1) % HERO_SLIDES.length);

  const slide = HERO_SLIDES[currentSlide];

  return (
    <section className="relative h-[600px] md:h-[700px] overflow-hidden">
      <AnimatePresence mode="wait">
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, scale: 1.05 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.6 }}
          className="absolute inset-0"
        >
          <img
            src={slide.image}
            alt="校园风光"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-sidebar/90 via-sidebar/60 to-transparent" />
          <div className="absolute inset-0 bg-gradient-to-t from-sidebar/50 via-transparent to-transparent" />
        </motion.div>
      </AnimatePresence>

      <div className="relative z-10 h-full flex items-center">
        <div className="container mx-auto px-6">
          <AnimatePresence mode="wait">
            <motion.div
              key={currentSlide}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="max-w-2xl"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-1 h-12 bg-chart-1 rounded-full" />
                <div>
                  <p className="text-chart-1 text-sm font-medium tracking-widest uppercase">Nankai University</p>
                  <p className="text-sidebar-foreground/70 text-xs">国家双一流A类高校 · 985 · 211</p>
                </div>
              </div>
              <h1
                className="text-5xl md:text-7xl font-bold text-white mb-2"
                style={{ fontFamily: "'Noto Serif SC', serif" }}
              >
                {slide.title}
              </h1>
              <h2
                className="text-3xl md:text-4xl font-semibold text-chart-1 mb-6"
                style={{ fontFamily: "'Noto Serif SC', serif" }}
              >
                {slide.subtitle}
              </h2>
              <p className="text-sidebar-foreground/90 text-base md:text-lg mb-8 leading-relaxed max-w-xl">
                {slide.description}
              </p>
              <div className="flex gap-4 flex-wrap">
                <Link to={slide.cta1.path}>
                  <Button
                    size="lg"
                    className="bg-primary hover:bg-primary/90 text-primary-foreground px-8"
                  >
                    {slide.cta1.label}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
                <Link to={slide.cta2.path}>
                  <Button
                    size="lg"
                    variant="outline"
                    className="border-white/40 text-white hover:bg-white/10 hover:border-white/60 px-8"
                  >
                    {slide.cta2.label}
                  </Button>
                </Link>
              </div>
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <button
        onClick={prevSlide}
        className="absolute left-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-all"
      >
        <ChevronLeft className="h-5 w-5" />
      </button>
      <button
        onClick={nextSlide}
        className="absolute right-4 top-1/2 -translate-y-1/2 z-20 w-10 h-10 rounded-full bg-white/20 hover:bg-white/40 flex items-center justify-center text-white transition-all"
      >
        <ChevronRight className="h-5 w-5" />
      </button>

      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex gap-2">
        {HERO_SLIDES.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`transition-all rounded-full ${
              index === currentSlide
                ? 'w-8 h-2 bg-chart-1'
                : 'w-2 h-2 bg-white/50 hover:bg-white/80'
            }`}
          />
        ))}
      </div>
    </section>
  );
}

function QuickNavSection() {
  return (
    <section className="bg-card border-b border-border">
      <div className="container mx-auto px-6 py-6">
        <div className="grid grid-cols-4 md:grid-cols-8 gap-4">
          {QUICK_NAV_ITEMS.map((item, index) => (
            <motion.div
              key={item.label}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.05 }}
            >
              <Link
                to={item.path}
                className="flex flex-col items-center gap-2 p-3 rounded-xl hover:bg-accent transition-all group"
              >
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${item.color} group-hover:scale-110 transition-transform`}>
                  <item.icon className="h-5 w-5" />
                </div>
                <span className="text-xs text-center text-foreground/80 font-medium">{item.label}</span>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function NewsSection() {
  const [activeTab, setActiveTab] = useState<string>('综合新闻');

  const filteredNews = NEWS_LIST.filter((n: NewsItem) =>
    activeTab === '综合新闻'
      ? ['综合新闻', '媒体南开'].includes(n.category)
      : n.category === activeTab
  ).slice(0, 6);

  const notices = NOTICES.slice(0, 6);

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h2
              className="text-3xl font-bold text-foreground"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              新闻动态
            </h2>
            <p className="text-muted-foreground mt-1">掌握南开最新资讯</p>
          </div>
          <Link to={ROUTE_PATHS.NEWS}>
            <Button variant="ghost" className="text-primary hover:text-primary/80">
              查看全部 <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-2">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="mb-6">
                <TabsTrigger value="综合新闻">综合新闻</TabsTrigger>
                <TabsTrigger value="学术活动">学术活动</TabsTrigger>
                <TabsTrigger value="通知公告">通知公告</TabsTrigger>
              </TabsList>
              <TabsContent value={activeTab}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {filteredNews.map((item, index) => (
                    <motion.div
                      key={item.id}
                      initial={{ opacity: 0, y: 15 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.07 }}
                    >
                      <NewsCard item={item} />
                    </motion.div>
                  ))}
                </div>
              </TabsContent>
            </Tabs>
          </div>

          <div>
            <div className="bg-card border border-border rounded-xl overflow-hidden">
              <div className="bg-primary px-5 py-4">
                <h3 className="font-semibold text-base" style={{color:'#3B82F6'}}>通知公告</h3>
              </div>
              <div className="divide-y divide-border">
                {notices.map((notice) => (
                  <NoticeItem key={notice.id} notice={notice} />
                ))}
              </div>
              <div className="px-5 py-3 bg-muted/50">
                <Link to={ROUTE_PATHS.NEWS} className="text-primary text-sm hover:underline flex items-center">
                  查看更多通知 <ArrowRight className="ml-1 h-3 w-3" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function StatsSection() {
  return (
    <section
      className="py-20 relative overflow-hidden"
      style={{
        background: 'linear-gradient(135deg, oklch(0.22 0.06 288) 0%, oklch(0.30 0.10 290) 50%, oklch(0.22 0.06 288) 100%)',
      }}
    >
      <div className="absolute inset-0 opacity-10">
        <div
          className="absolute inset-0"
          style={{
            backgroundImage:
              'radial-gradient(circle at 25% 25%, oklch(0.72 0.14 57) 0%, transparent 50%), radial-gradient(circle at 75% 75%, oklch(0.75 0.10 290) 0%, transparent 50%)',
          }}
        />
      </div>
      <div className="container mx-auto px-6 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2
            className="text-3xl font-bold text-white mb-3"
            style={{ fontFamily: "'Noto Serif SC', serif" }}
          >
            南开数字
          </h2>
          <p className="text-sidebar-foreground/70">以数字见证南开的卓越与辉煌</p>
        </motion.div>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {STATS.map((stat, index) => (
            <motion.div
              key={stat.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.08, duration: 0.5 }}
            >
              <StatCard stat={stat} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CollegesSection() {
  const featuredColleges = COLLEGES.slice(0, 6);

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="flex items-center justify-between mb-8"
        >
          <div>
            <h2
              className="text-3xl font-bold text-foreground"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              院系设置
            </h2>
            <p className="text-muted-foreground mt-1">探索南开多元学科体系</p>
          </div>
          <Link to={ROUTE_PATHS.ACADEMICS}>
            <Button variant="ghost" className="text-primary hover:text-primary/80">
              查看全部院系 <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {featuredColleges.map((college, index) => (
            <motion.div
              key={college.id}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.07, duration: 0.4 }}
            >
              <CollegeCard college={college} />
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

function ResearchHighlightSection() {
  return (
    <section className="py-16 bg-muted/40">
      <div className="container mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src={IMAGES.RESEARCH_5}
                alt="科学研究"
                className="w-full h-80 object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-sidebar/70 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6">
                <Badge className="bg-chart-1/90 text-white border-0 mb-2">国家重点实验室</Badge>
                <p className="text-white font-semibold text-lg">前沿科学探索，引领学术高峰</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4">
              <div className="relative rounded-xl overflow-hidden">
                <img src={IMAGES.LIBRARY_3} alt="图书馆" className="w-full h-36 object-cover" />
                <div className="absolute inset-0 bg-primary/20" />
              </div>
              <div className="relative rounded-xl overflow-hidden">
                <img src={IMAGES.CAMPUS_6} alt="校园" className="w-full h-36 object-cover" />
                <div className="absolute inset-0 bg-primary/20" />
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <p className="text-chart-1 text-sm font-medium tracking-widest uppercase mb-3">Academic Excellence</p>
            <h2
              className="text-3xl md:text-4xl font-bold text-foreground mb-5"
              style={{ fontFamily: "'Noto Serif SC', serif" }}
            >
              科研创新，追求卓越
            </h2>
            <p className="text-muted-foreground leading-relaxed mb-6">
              南开大学拥有国家重点实验室6个、教育部重点实验室8个，年均承担国家级科研项目超过1500项。
              化学、材料科学、数学、物理、经济学等优势学科在国际权威学术排名中名列前茅。
            </p>
            <div className="space-y-4 mb-8">
              {[
                { label: '化学学科', desc: '连续多年入选ESI全球前1‰，国家级奖项丰硕', color: 'bg-chart-1' },
                { label: '数学科学', desc: '陈省身数学研究所享誉国际，几何拓扑领域世界一流', color: 'bg-primary' },
                { label: '经济研究', desc: '经济学科是中国近代经济学教育与研究的摇篮', color: 'bg-chart-4' },
              ].map((item) => (
                <div key={item.label} className="flex gap-4 items-start">
                  <div className={`w-1 h-12 ${item.color} rounded-full flex-shrink-0 mt-1`} />
                  <div>
                    <h4 className="font-semibold text-foreground">{item.label}</h4>
                    <p className="text-muted-foreground text-sm">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
            <Link to={ROUTE_PATHS.RESEARCH}>
              <Button className="bg-primary hover:bg-primary/90 text-primary-foreground">
                探索科研成果 <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </div>
    </section>
  );
}

function InternationalSection() {
  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-chart-1 text-sm font-medium tracking-widest uppercase mb-2">International Cooperation</p>
          <h2
            className="text-3xl font-bold text-foreground mb-3"
            style={{ fontFamily: "'Noto Serif SC', serif" }}
          >
            国际合作与交流
          </h2>
          <p className="text-muted-foreground max-w-xl mx-auto">
            南开大学与全球280余所著名高校建立了战略合作关系，积极推进国际化办学，拓宽师生国际视野。
          </p>
        </motion.div>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {PARTNER_UNIVERSITIES.map((uni, index) => (
            <motion.div
              key={uni}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.05 }}
              className="bg-card border border-border rounded-xl p-4 flex items-center justify-center hover:border-primary/40 hover:shadow-md transition-all group cursor-default"
            >
              <div className="text-center">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2 group-hover:bg-primary/20 transition-colors">
                  <Globe className="h-5 w-5 text-primary" />
                </div>
                <p className="text-xs text-muted-foreground text-center leading-tight">{uni}</p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="text-center mt-10"
        >
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            {[
              { num: '280+', label: '合作院校' },
              { num: '50+', label: '合作国家和地区' },
              { num: '1000+', label: '年均国际交流学生' },
              { num: '200+', label: '国际联合科研项目' },
            ].map((item) => (
              <div key={item.label} className="text-center">
                <div
                  className="text-3xl font-bold text-chart-1"
                  style={{ fontFamily: "'Source Code Pro', monospace" }}
                >
                  {item.num}
                </div>
                <div className="text-muted-foreground text-sm">{item.label}</div>
              </div>
            ))}
          </div>
          <Link to={ROUTE_PATHS.ADMISSIONS}>
            <Button variant="outline" className="border-primary text-primary hover:bg-primary/10">
              了解留学南开 <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </motion.div>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <Layout>
      <HeroSection />
      <QuickNavSection />
      <NewsSection />
      <StatsSection />
      <CollegesSection />
      <ResearchHighlightSection />
      <InternationalSection />
    </Layout>
  );
}
